/**
 * Contract Service
 *
 * Type-safe contract management service using @monorepo/shared schemas
 */

import type {
  BillingPeriod,
  ContractStatus,
} from '@monorepo/shared/schemas/common'
import {
  type ApplyCouponToContract,
  ApplyCouponToContractSchema,
  type CancelContract,
  type ChangePlan,
  ChangePlanSchema,
  type Contract,
  type ContractAddonReference,
  type ContractAddon as ContractAddonRequest,
  type ContractDiscount,
  type ContractDiscountResponse,
  ContractDiscountResponseSchema,
  type ContractLineItemOverride,
  type ContractQuery,
  type ContractResponse,
  ContractResponseSchema,
  type ContractTax,
  type CouponValidationResponse,
  CouponValidationResponseSchema,
  type CreateContract,
  CreateContractSchema,
  type LineItemResponse,
  LineItemResponseSchema,
  type PaginatedContractsResponse,
  PaginatedContractsResponseSchema,
  type SuspendContract,
  SuspendContractSchema,
  type UpdateContract,
  UpdateContractSchema,
  type ValidateCouponForContract,
  ValidateCouponForContractSchema,
} from '@monorepo/shared/schemas/contracts'
import { del, get, patch, post } from '@/lib/api-client'

// ============================================================================
// API RESPONSE TYPES (Not yet in shared schema)
// TODO: Move these to @monorepo/shared/schemas/contracts
// ============================================================================

export interface ContractAddon {
  id: string
  contract_id?: string
  plan_id?: string
  name: string
  description?: string
  type: 'one_time' | 'recurring' | 'usage_based' | 'pending' | string
  amount: number
  currency: string
  quantity: number
  proration?: boolean
  billing_interval?: string
  start_date?: string
  end_date?: string
  status: 'active' | 'inactive' | 'canceled' | 'pending'
  metadata?: Record<string, unknown>
  agents?: Record<string, unknown>[]
  features?: Record<string, unknown>[]
  charges?: Record<string, unknown>[]
  monthly_charges?: number
  created_at?: string
  updated_at?: string
}

export interface ContractTaxResponse {
  id: string
  tax_id: string
  // Catalog tax fields (from JOIN with catalog.taxes)
  name: string
  code: string
  description?: string
  tax_type: 'percentage' | 'fixed' | 'compound'
  rate: number
  percentage_value?: number
  fixed_amount_cents?: number
  applies_to: string
  taxable_amount_type?: string
  compound: boolean
  jurisdiction?: string
  jurisdiction_level?: string
  tax_authority?: string
  effective_from?: string
  effective_to?: string
  // Contract-specific overrides
  priority: number
  is_active: boolean
  autoApply: boolean
  auto_apply: boolean
  currency?: string
  status: string
  source: 'contract'
  assigned_at?: string
  created_at?: string
  updated_at?: string
}

export interface ContractWithDetails extends ContractResponse {
  line_items: LineItemResponse[]
  addons: ContractAddon[]
  discounts: ContractDiscountResponse[]
  taxes: ContractTaxResponse[]
}

export interface AddonPlan {
  id: string
  name: string
  description?: string
  type: 'addon'
  currency: string
  billing_interval: 'monthly' | 'yearly' | 'usage'
  status: 'draft' | 'active' | 'archived'
  agents: Record<string, unknown>[]
  features: Record<string, unknown>[]
  charges: Record<string, unknown>[]
  created_at: string
  updated_at: string
}

export interface AddAddonRequest {
  plan_id: string
  quantity?: number
  custom_price?: number
  start_date?: string
  end_date?: string
  metadata?: Record<string, unknown>
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class ContractService {
  /**
   * Get contracts with optional filters
   */
  async getContracts(
    query?: ContractQuery
  ): Promise<PaginatedContractsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.customer_id) queryParams.append('customer_id', query.customer_id)
    if (query?.plan_id) queryParams.append('plan_id', query.plan_id)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.billing_interval)
      queryParams.append('billing_interval', query.billing_interval)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())
    if (query?.sort) queryParams.append('sort', query.sort)
    if (query?.order) queryParams.append('order', query.order)

    const url = `/contracts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedContractsResponseSchema)
  }

  /**
   * Get contract by ID
   */
  async getContractById(id: string): Promise<ContractResponse> {
    return get(`/contracts/${id}`, ContractResponseSchema)
  }

  /**
   * Get contract by ID (alias for getContractById)
   */
  async getContract(id: string): Promise<ContractResponse> {
    return this.getContractById(id)
  }

  /**
   * Get contract with all details (line items, addons, discounts, taxes)
   */
  async getContractWithDetails(id: string): Promise<ContractWithDetails> {
    // Note: API response includes all relations, no schema validation yet
    return get<ContractWithDetails>(
      `/contracts/${id}?include=line_items,addons,discounts,taxes`
    )
  }

  /**
   * Create new contract
   */
  async createContract(data: CreateContract): Promise<ContractResponse> {
    const validated = CreateContractSchema.parse(data)

    return post('/contracts', validated, ContractResponseSchema)
  }

  /**
   * Update contract
   */
  async updateContract(
    id: string,
    data: UpdateContract
  ): Promise<ContractResponse> {
    const validated = UpdateContractSchema.parse(data)

    return patch(`/contracts/${id}`, validated, ContractResponseSchema)
  }

  /**
   * Delete contract
   */
  async deleteContract(id: string): Promise<void> {
    await del<void>(`/contracts/${id}`)
  }

  /**
   * Activate contract
   */
  async activateContract(id: string): Promise<ContractResponse> {
    return post(`/contracts/${id}/activate`, {}, ContractResponseSchema)
  }

  /**
   * Pause contract
   */
  async pauseContract(id: string): Promise<ContractResponse> {
    return post(`/contracts/${id}/pause`, {}, ContractResponseSchema)
  }

  /**
   * Cancel contract
   */
  async cancelContract(
    id: string,
    reason?: string,
    cancellationType: 'immediate' | 'end_of_period' = 'immediate'
  ): Promise<ContractResponse> {
    const payload: CancelContract = {
      cancellation_type: cancellationType,
      reason,
    }

    return post(`/contracts/${id}/cancel`, payload, ContractResponseSchema)
  }

  /**
   * Suspend contract
   */
  async suspendContract(
    id: string,
    data?: SuspendContract
  ): Promise<ContractResponse> {
    const validated = data ? SuspendContractSchema.parse(data) : {}

    return post(`/contracts/${id}/suspend`, validated, ContractResponseSchema)
  }

  /**
   * Resume suspended contract
   */
  async resumeContract(id: string): Promise<ContractResponse> {
    return post(`/contracts/${id}/resume`, {}, ContractResponseSchema)
  }

  /**
   * Change plan for contract
   */
  async changePlan(id: string, data: ChangePlan): Promise<ContractResponse> {
    const validated = ChangePlanSchema.parse(data)

    return post(
      `/contracts/${id}/change-plan`,
      validated,
      ContractResponseSchema
    )
  }

  /**
   * Apply coupon to contract
   */
  async applyCoupon(
    contractId: string,
    data: ApplyCouponToContract
  ): Promise<ContractResponse> {
    const validated = ApplyCouponToContractSchema.parse(data)

    return post(
      `/contracts/${contractId}/coupons`,
      validated,
      ContractResponseSchema
    )
  }

  /**
   * Validate coupon for contract
   */
  async validateCoupon(
    contractId: string,
    data: ValidateCouponForContract
  ): Promise<CouponValidationResponse> {
    const validated = ValidateCouponForContractSchema.parse(data)

    return post(
      `/contracts/${contractId}/coupons/validate`,
      validated,
      CouponValidationResponseSchema
    )
  }

  /**
   * Remove discount from contract
   */
  async removeDiscount(
    contractId: string,
    discountId: string
  ): Promise<ContractResponse> {
    return del<ContractResponse>(
      `/contracts/${contractId}/discounts/${discountId}`
    )
  }

  // ============================================================================
  // LINE ITEMS
  // ============================================================================

  /**
   * Get contract line items
   */
  async getLineItems(contractId: string): Promise<LineItemResponse[]> {
    const response = await get<{ data: LineItemResponse[] }>(
      `/contracts/${contractId}/line-items`
    )
    return response.data
  }

  /**
   * Add line item to contract
   */
  async addLineItem(
    contractId: string,
    lineItem: Omit<LineItemResponse, 'id' | 'contract_id'>
  ): Promise<LineItemResponse> {
    return post(
      `/contracts/${contractId}/line-items`,
      lineItem,
      LineItemResponseSchema
    )
  }

  /**
   * Update line item
   */
  async updateLineItem(
    contractId: string,
    lineItemId: string,
    updates: Partial<LineItemResponse>
  ): Promise<LineItemResponse> {
    return patch(
      `/contracts/${contractId}/line-items/${lineItemId}`,
      updates,
      LineItemResponseSchema
    )
  }

  /**
   * Delete line item
   */
  async deleteLineItem(contractId: string, lineItemId: string): Promise<void> {
    await del<void>(`/contracts/${contractId}/line-items/${lineItemId}`)
  }

  // ============================================================================
  // ADDONS
  // ============================================================================

  /**
   * Get available addon plans
   */
  async getAvailableAddons(): Promise<AddonPlan[]> {
    const response = await get<{
      data: AddonPlan[]
      meta: Record<string, unknown>
    }>('/plans?category=addon&status=active&page=1&limit=50')
    return response.data
  }

  /**
   * Get contract addons
   */
  async getAddons(contractId: string): Promise<ContractAddon[]> {
    const response = await get<{
      data: Array<{
        id: string
        addon_plan_id: string
        addon_name: string
        addon_code: string
        addon_description?: string
        quantity: number
        discount_percentage?: number
        metadata?: Record<string, unknown>
        created_at?: string
        updated_at?: string
      }>
    }>(`/contracts/${contractId}/addons`)

    // Map API response fields to ContractAddon interface
    return response.data.map(addon => ({
      id: addon.id,
      plan_id: addon.addon_plan_id,
      name: addon.addon_name,
      description: addon.addon_description,
      type: 'addon' as const,
      amount: 0, // Will be calculated from line items
      currency: 'USD',
      quantity: addon.quantity,
      status: 'active' as const,
      metadata: addon.metadata,
    }))
  }

  /**
   * Add addon to contract
   */
  async addAddon(
    contractId: string,
    addon: AddAddonRequest
  ): Promise<ContractAddon> {
    return post<ContractAddon>(`/contracts/${contractId}/addons`, addon)
  }

  /**
   * Update contract addon
   */
  async updateAddon(
    contractId: string,
    addonId: string,
    updates: Partial<ContractAddon>
  ): Promise<ContractAddon> {
    return patch<ContractAddon>(
      `/contracts/${contractId}/addons/${addonId}`,
      updates
    )
  }

  /**
   * Delete addon from contract
   */
  async deleteAddon(contractId: string, addonId: string): Promise<void> {
    await del<void>(`/contracts/${contractId}/addons/${addonId}`)
  }

  /**
   * Get addon line items
   */
  async getAddonLineItems(
    contractId: string,
    addonId?: string
  ): Promise<LineItemResponse[]> {
    const response = await get<{ data: LineItemResponse[] }>(
      `/contracts/${contractId}/line-items`
    )
    return response.data.filter(item => {
      if (item.type !== 'addon') return false
      if (addonId && item.addon_id !== addonId) return false
      return true
    })
  }

  /**
   * Get contract with addons
   */
  async getContractWithAddons(
    contractId: string
  ): Promise<ContractResponse & { addons: ContractAddon[] }> {
    const [contract, addons] = await Promise.all([
      this.getContractById(contractId),
      this.getAddons(contractId),
    ])

    return { ...contract, addons }
  }

  // ============================================================================
  // DISCOUNTS
  // ============================================================================

  /**
   * Get contract discounts
   */
  async getDiscounts(contractId: string): Promise<ContractDiscountResponse[]> {
    const response = await get<{ data: ContractDiscountResponse[] }>(
      `/contracts/${contractId}/discounts`
    )
    return response.data
  }

  /**
   * Add discount to contract
   */
  async addDiscount(
    contractId: string,
    discount: ContractDiscount
  ): Promise<ContractDiscountResponse> {
    return post(
      `/contracts/${contractId}/discounts`,
      discount,
      ContractDiscountResponseSchema
    )
  }

  /**
   * Update contract discount
   */
  async updateDiscount(
    contractId: string,
    discountId: string,
    updates: Partial<ContractDiscount>
  ): Promise<ContractDiscountResponse> {
    return patch(
      `/contracts/${contractId}/discounts/${discountId}`,
      updates,
      ContractDiscountResponseSchema
    )
  }

  /**
   * Delete discount from contract
   */
  async deleteDiscount(contractId: string, discountId: string): Promise<void> {
    await del<void>(`/contracts/${contractId}/discounts/${discountId}`)
  }

  // ============================================================================
  // TAXES
  // ============================================================================

  /**
   * Get contract taxes
   */
  async getTaxes(contractId: string): Promise<ContractTaxResponse[]> {
    const response = await get<{ data: ContractTaxResponse[] }>(
      `/contracts/${contractId}/taxes`
    )
    return response.data
  }

  /**
   * Add tax to contract
   */
  async addTax(
    contractId: string,
    tax: ContractTax
  ): Promise<ContractTaxResponse> {
    return post<ContractTaxResponse>(`/contracts/${contractId}/taxes`, tax)
  }

  /**
   * Update contract tax
   */
  async updateTax(
    contractId: string,
    taxId: string,
    updates: Partial<ContractTax>
  ): Promise<ContractTaxResponse> {
    return patch<ContractTaxResponse>(
      `/contracts/${contractId}/taxes/${taxId}`,
      updates
    )
  }

  /**
   * Delete tax from contract
   */
  async deleteTax(contractId: string, taxId: string): Promise<void> {
    await del<void>(`/contracts/${contractId}/taxes/${taxId}`)
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Calculate addon charges based on line items
   */
  calculateAddonCharges(lineItems: LineItemResponse[]): number {
    return lineItems.reduce((total, item) => {
      if (
        item.line_item_type === 'setup' ||
        item.line_item_type === 'recurring'
      ) {
        return total + item.unit_price
      } else {
        const minCommitment = item.minimum_commitment || 0
        if (minCommitment > 0) {
          if (
            (item.pricing_model === 'tiered' ||
              item.pricing_model === 'package') &&
            (item.metadata as Record<string, unknown>)?.tiers
          ) {
            let cost = 0
            let remainingUnits = minCommitment
            const tiers = ((item.metadata as Record<string, unknown>).tiers ||
              []) as Record<string, unknown>[]

            for (const tier of tiers) {
              if (remainingUnits <= 0) break
              const tierMax =
                tier.max_units === null ? Infinity : Number(tier.max_units)
              const tierMin = Number(tier.min_units) || 1
              const tierRange =
                tierMax === Infinity
                  ? remainingUnits
                  : (tierMax as number) - (tierMin as number) + 1
              const unitsInThisTier = Math.min(remainingUnits, tierRange)
              const tierPrice = parseFloat(String(tier.unit_price)) || 0
              cost += unitsInThisTier * tierPrice
              remainingUnits -= unitsInThisTier
            }
            return total + cost
          } else {
            return total + minCommitment * item.unit_price
          }
        }
        return total
      }
    }, 0)
  }

  /**
   * Calculate monthly total for an addon plan
   */
  calculateAddonPlanMonthlyTotal(addonPlan: AddonPlan): number {
    if (!addonPlan) return 0

    let monthlyTotal = 0

    if (addonPlan.charges) {
      const recurringCharges = addonPlan.charges.filter(
        (charge: Record<string, unknown>) => charge.type === 'recurring'
      )
      monthlyTotal += recurringCharges.reduce(
        (total: number, charge: Record<string, unknown>) => {
          return total + (parseFloat(String(charge.amount)) || 0)
        },
        0
      )
    }

    if (addonPlan.agents) {
      for (const agent of addonPlan.agents) {
        if (agent.signals) {
          for (const signal of agent.signals as Record<string, unknown>[]) {
            const minCommitment = Number(signal.minimum_commitment_units) || 0
            if (minCommitment > 0) {
              if (
                signal.pricing_model === 'tiered' &&
                signal.tiers &&
                (signal.tiers as unknown[]).length > 0
              ) {
                let cost = 0
                let remainingUnits = minCommitment

                for (const tier of signal.tiers as Record<string, unknown>[]) {
                  if (remainingUnits <= 0) break
                  const tierMax =
                    tier.maxUnits === null || tier.maxUnits === undefined
                      ? Number.POSITIVE_INFINITY
                      : Number(tier.maxUnits)
                  const tierMin = Number(tier.minUnits) || 1
                  const tierRange =
                    tierMax === Number.POSITIVE_INFINITY
                      ? remainingUnits
                      : tierMax - tierMin + 1
                  const unitsInThisTier = Math.min(remainingUnits, tierRange)
                  const tierPrice = parseFloat(String(tier.unitPrice)) || 0
                  cost += unitsInThisTier * tierPrice
                  remainingUnits -= unitsInThisTier
                }
                monthlyTotal += cost
              } else if (
                signal.pricing_model === 'volume' &&
                signal.tiers &&
                (signal.tiers as unknown[]).length > 0
              ) {
                const applicableTier = (
                  signal.tiers as Record<string, unknown>[]
                ).find(
                  (tier: Record<string, unknown>) =>
                    minCommitment >= (Number(tier.minUnits) || 1) &&
                    (tier.maxUnits === null ||
                      minCommitment <= Number(tier.maxUnits))
                )
                if (applicableTier) {
                  const tierPrice =
                    parseFloat(String(applicableTier.unitPrice)) || 0
                  monthlyTotal += minCommitment * tierPrice
                }
              } else {
                const unitPrice = Number(signal.unit_price) || 0
                monthlyTotal += minCommitment * unitPrice
              }
            }
          }
        }
      }
    }

    if (addonPlan.features) {
      for (const feature of addonPlan.features) {
        const minCommitment = Number(feature.minimum_commitment_units) || 0
        if (minCommitment > 0) {
          if (
            feature.pricing_model === 'tiered' &&
            feature.tier_pricing &&
            (feature.tier_pricing as unknown[]).length > 0
          ) {
            let cost = 0
            let remainingUnits = minCommitment

            for (const tier of feature.tier_pricing as Record<
              string,
              unknown
            >[]) {
              if (remainingUnits <= 0) break
              const tierMax =
                tier.lastUnit === null || tier.lastUnit === undefined
                  ? Number.POSITIVE_INFINITY
                  : Number(tier.lastUnit)
              const tierMin = Number(tier.firstUnit) || 1
              const tierRange =
                tierMax === Number.POSITIVE_INFINITY
                  ? remainingUnits
                  : tierMax - tierMin + 1
              const unitsInThisTier = Math.min(remainingUnits, tierRange)
              const tierPrice = Number(tier.perUnitPrice) || 0
              cost += unitsInThisTier * tierPrice
              remainingUnits -= unitsInThisTier
            }
            monthlyTotal += cost
          } else if (
            feature.pricing_model === 'volume' &&
            feature.tier_pricing &&
            (feature.tier_pricing as unknown[]).length > 0
          ) {
            const applicableTier = (
              feature.tier_pricing as Record<string, unknown>[]
            ).find(
              (tier: Record<string, unknown>) =>
                minCommitment >= (Number(tier.firstUnit) || 1) &&
                (tier.lastUnit === null ||
                  minCommitment <= Number(tier.lastUnit))
            )
            if (applicableTier) {
              const tierPrice = Number(applicableTier.perUnitPrice) || 0
              monthlyTotal += minCommitment * tierPrice
            }
          } else {
            const unitPrice = Number(feature.unit_price) || 0
            monthlyTotal += minCommitment * unitPrice
          }
        }
      }
    }

    return monthlyTotal
  }
}

export const contractService = new ContractService()

// Re-export types for convenience
export type {
  Contract,
  ContractResponse,
  CreateContract,
  UpdateContract,
  PaginatedContractsResponse,
  ContractQuery,
  LineItemResponse as ContractLineItem,
  ContractDiscountResponse,
  ContractStatus,
  BillingPeriod,
  ContractLineItemOverride,
  ContractAddonRequest,
  ContractAddonReference,
  ContractTax,
  ContractDiscount,
  CancelContract,
}
